import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField.tsx';
import debounce from 'lodash/debounce';
import { useMemo } from 'react';
import { type FieldArrayWithId, type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AudienceShares } from '../../types.ts';

type FeeSummaryProps = {
  audienceName: string;
  itemIndex: number;
  field: FieldArrayWithId<AudienceShares, 'audience_share', 'id'>;
};

export const FeeSummary = ({ itemIndex, field, audienceName }: FeeSummaryProps) => {
  const { t } = useTranslation();
  const {
    setValue,
    formState: { errors },
  } = useFormContext<AudienceShares>();

  const textFieldName = `audience_share.${itemIndex}.value` satisfies Path<AudienceShares>;
  const sectionName = field.revenue_method === 'CPM' ? t('Fixed CPM') : t('Percentage of Media');

  const debouncedUpdate = useMemo(
    () =>
      debounce((val: string) => {
        setValue(textFieldName, val, { shouldDirty: true, shouldTouch: true });
      }, 500),
    [setValue, textFieldName]
  );

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Typography variant="h3">{sectionName}</Typography>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 'fontWeightRegular',
        }}
      >
        {t('Audience')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: 0.7,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <Typography
            noWrap
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {audienceName}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 0.3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            position: 'relative',
            mt: -1.5,
          }}
        >
          <FormControl fullWidth margin="dense" variant="outlined" sx={{ position: 'relative', mt: -1 }}>
            <FormLabel htmlFor={textFieldName} error={!!errors.audience_share?.[itemIndex]}>
              {field.revenue_method === 'CPM' ? t('CPM') : t('Fee')}
            </FormLabel>

            <RHFTextField
              id={textFieldName}
              name={textFieldName}
              onChange={(_, e) => debouncedUpdate(e.target.value)}
              variant="outlined"
              size="small"
              placeholder={field.revenue_method === 'CPM' ? '0.00' : '0'}
              slotProps={{
                input: {
                  startAdornment:
                    field.revenue_method === 'CPM' ? <InputAdornment position="start">$</InputAdornment> : undefined,
                  endAdornment:
                    field.revenue_method === 'PoM' ? <InputAdornment position="end">%</InputAdornment> : undefined,
                },
                htmlInput: {
                  'data-test': 'fee-summary-input',
                },
              }}
            />
          </FormControl>

          <Tooltip
            title={
              field.revenue_method === 'PoM'
                ? t(
                    'Bid CPM is reduced by partner fee % (i.e. $1 CPM bid with 10% fee results in $0.90 bid & $0.10 partner fee)'
                  )
                : t(
                    'Bid CPM is reduced by the partner fixed CPM fee (i.e. $1.50 CPM and a $0.50 fixed CPM fee results in a $1 bid and a $0.50 partner fee)'
                  )
            }
            sx={{ mt: 1.25 }}
          >
            <IconButton size="large">
              <InfoRoundedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};
